import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useFollowManager } from './FollowManager';
import AuthPrompt from './AuthPrompt';

const ProfileView = ({ userMetadata, pubkey, onClose, relays }) => {
  const { publicKey } = useAuth();
  const { isFollowing, follow, unfollow, following, followers } = useFollowManager(relays);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Handle escape key
  useEffect(() => {
	const handleEscape = (e) => {
	  if (e.key === 'Escape') {
		handleClose();
	  }
	};
	
	// Prevent body scroll when modal is open
	document.body.style.overflow = 'hidden';
	window.addEventListener('keydown', handleEscape);
	
	return () => {
	  document.body.style.overflow = 'unset';
	  window.removeEventListener('keydown', handleEscape);
	};
  }, []);

  const handleClose = () => {
	setIsClosing(true);
	setTimeout(() => {
	  onClose();
	}, 200); // Match this with CSS animation duration
  };

  const handleFollow = async () => {
	if (!publicKey) {
	  setShowAuthPrompt(true);
	  return;
	}

	setIsLoading(true);
	if (isFollowing(pubkey)) {
	  await unfollow(pubkey);
	} else {
	  await follow(pubkey);
	}
	setIsLoading(false);
  };

  return (
	<div 
	  className={`profile-modal-overlay ${isClosing ? 'closing' : ''}`}
	  onClick={handleClose}
	>
	  <div 
		className={`profile-modal ${isClosing ? 'closing' : ''}`}
		onClick={e => e.stopPropagation()}
	  >
		<div className="profile-modal-header">
		  <button 
			className="back-button"
			onClick={handleClose}
		  >
			← Back
		  </button>
		</div>

		<div className="profile-modal-content">
		  {userMetadata?.picture && (
			<img 
			  src={userMetadata.picture} 
			  alt="Profile" 
			  className="profile-picture-large"
			  onError={(e) => e.target.style.display = 'none'}
			/>
		  )}
		  
		  <h2 className="profile-name">
			{userMetadata?.name || userMetadata?.display_name || 'Anonymous'}
		  </h2>
		  
		  {userMetadata?.nip05 && (
			<div className="profile-nip05">
			  {userMetadata.nip05}
			</div>
		  )}

		  <div className="profile-stats">
			<div className="stat-item">
			  <div className="stat-value">{following.size}</div>
			  <div className="stat-label">Following</div>
			</div>
			<div className="stat-item">
			  <div className="stat-value">{followers.size}</div>
			  <div className="stat-label">Followers</div>
			</div>
		  </div>
		  
		  {publicKey !== pubkey && (
			<button 
			  className={`follow-button ${isFollowing(pubkey) ? 'following' : ''}`}
			  onClick={handleFollow}
			  disabled={isLoading}
			>
			  {isLoading ? 'Loading...' : isFollowing(pubkey) ? 'Following' : 'Follow'}
			</button>
		  )}
		  
		  {userMetadata?.about && (
			<p className="profile-about">
			  {userMetadata.about}
			</p>
		  )}
		</div>
	  </div>

	  {showAuthPrompt && (
		<AuthPrompt onClose={() => setShowAuthPrompt(false)} />
	  )}
	</div>
  );
};

export default ProfileView;